import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatEUR, formatEURDec, formatDate, statusClass } from "@/lib/format";
import { ArrowLeft, Receipt } from "lucide-react";

export default function QuoteDetail() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/quotes/${id}`)
      .then((r) => setQuote(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [id]);

  if (err) {
    return (
      <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">
        {err}
      </div>
    );
  }
  if (!quote) return <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>;

  return (
    <div data-testid={TID.quoteDetail} className="space-y-6">
      <Link
        to="/app/quotes"
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500 hover:text-[#EDEDED]"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
        Torna a Preventivi
      </Link>

      <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-5 py-4 flex items-start justify-between bg-[#141414]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-2">
              <Receipt className="w-3 h-3 text-[#0055FF]" strokeWidth={1.5} />
              preventivo · {quote.id}
            </div>
            <h2 className="font-heading uppercase text-2xl tracking-tight mt-1">
              {quote.client_name} · {quote.project_name}
            </h2>
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${statusClass(quote.status)}`}>
            {quote.status}
          </span>
        </header>

        <div className="px-5 py-3 border-b border-[#1F1F1F] font-mono text-[11px] text-neutral-500 flex flex-wrap gap-x-6 gap-y-1">
          <div>lead · <span className="text-neutral-300">{quote.lead_id}</span></div>
          <div>data · <span className="text-neutral-300">{formatDate(quote.created_at)}</span></div>
          {quote.valid_until && (
            <div>valido fino · <span className="text-neutral-300">{formatDate(quote.valid_until)}</span></div>
          )}
        </div>

        <table data-testid={TID.quoteItemsTable} className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F] font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="text-left px-5 py-2">Voce</th>
              <th className="text-right px-5 py-2 w-16">Q.tà</th>
              <th className="text-right px-5 py-2 w-36">Prezzo unit.</th>
              <th className="text-right px-5 py-2 w-32">Importo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            {quote.items.map((it, i) => (
              <tr key={i} className="hover:bg-[#141414]">
                <td className="px-5 py-3 text-sm text-[#EDEDED]">{it.label}</td>
                <td className="px-5 py-3 font-mono text-xs text-right text-neutral-300">{it.qty}</td>
                <td className="px-5 py-3 font-mono text-xs text-right text-neutral-300">{formatEURDec(it.unit_price_eur)}</td>
                <td className="px-5 py-3 font-mono text-sm text-right text-[#EDEDED]">{formatEURDec(it.amount_eur)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-[#1F1F1F]">
              <td colSpan={3} className="px-5 py-2 text-right font-mono text-[11px] uppercase tracking-widest text-neutral-500">Subtotale</td>
              <td className="px-5 py-2 text-right font-mono text-sm">{formatEURDec(quote.subtotal_eur)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="px-5 py-2 text-right font-mono text-[11px] uppercase tracking-widest text-neutral-500">
                IVA ({(quote.vat_rate * 100).toFixed(0)}%)
              </td>
              <td className="px-5 py-2 text-right font-mono text-sm">{formatEURDec(quote.vat_eur)}</td>
            </tr>
            <tr data-testid={TID.quoteTotalRow} className="border-t border-[#1F1F1F] bg-[#141414]">
              <td colSpan={3} className="px-5 py-3 text-right font-mono text-xs uppercase tracking-[0.24em] text-[#F5A623]">Totale</td>
              <td className="px-5 py-3 text-right font-mono text-lg text-[#F5A623] font-semibold">
                {formatEURDec(quote.total_eur)}
              </td>
            </tr>
          </tfoot>
        </table>
      </section>
    </div>
  );
}
